<template>
  <div class="azure-boards-step-3">
    <div class="row">
      <div class="col-12">
        <h2>Work Item Type Mapping</h2>
      </div>
    </div>

    <div v-if="loadError" class="error-state">
      <LucideIcon icon="TriangleAlert" class="error-icon"/>
      <p>{{ loadErrorMessage }}</p>
      <ButtonElement class="btn--ms btn--accent btn--md" @click="loadWorkItemTypes()">
        <LucideIcon icon="RotateCw"/> Retry
      </ButtonElement>
    </div>

    <template v-else>
      <div class="row jira-feature-headline jf-row">
        <div class="col-5">
          <div class="jfh-col metastory">
            <div class="icon">
              <img class="metastory-logo light" src="@/assets/ui/text-cursor.svg" style="width: 6px;">
              <img class="metastory-logo dark" src="@/assets/ui/text-cursor-white.svg" style="width: 6px;">
            </div>
            metastory AI Types
          </div>
        </div>
        <div class="col-2 icon-snyc"></div>
        <div class="col-5">
          <div class="jfh-col">
            <div class="icon">
              <img src="@/assets/integrations/azure.svg" style="height: 16px;">
            </div>
            Work Item Types
          </div>
        </div>
      </div>

      <div class="row jira-feature-form jf-row">
        <div class="col-5">
          <div class="fp-label">
            <span class="icon"><div class="color"></div></span>
            <span>Group</span>
          </div>
        </div>
        <div class="col-2 icon-snyc">
          <LucideIcon icon="Link"/>
        </div>
        <div class="col-5">
          <FieldSkeletonComponent v-if="isLoading"/>
          <JiraMultipleSelectDropdownElement
            v-else
            v-model="selectedGroups"
            :options="groupActions"
            title="Multiple Select"
          />
        </div>
      </div>

      <div class="row jira-feature-form jf-row">
        <div class="col-5">
          <div class="fp-label">
            <span class="icon"><LucideIcon icon="FileText"/></span>
            <span>Feature</span>
          </div>
        </div>
        <div class="col-2 icon-snyc">
          <LucideIcon icon="Link"/>
        </div>
        <div class="col-5">
          <FieldSkeletonComponent v-if="isLoading"/>
          <JiraMultipleSelectDropdownElement
            v-else
            v-model="selectedFeatures"
            :options="featureActions"
            title="Multiple Select"
          />
        </div>
      </div>

      <div class="row jira-feature-form jf-row">
        <div class="col-5">
          <div class="fp-label">
            <span class="icon"><LucideIcon icon="FileText"/></span>
            <span>Subfeature</span>
          </div>
        </div>
        <div class="col-2 icon-snyc">
          <LucideIcon icon="Link"/>
        </div>
        <div class="col-5">
          <FieldSkeletonComponent v-if="isLoading"/>
          <JiraMultipleSelectDropdownElement
            v-else
            v-model="selectedSubfeatures"
            :options="subfeatureActions"
            title="Multiple Select"
          />
        </div>
      </div>
    </template>

    <div class="row">
      <div class="col-12">
        <div class="config-footer flex-end">
          <div class="button-group">
            <ButtonElement class="btn--ms btn--silent-tertiary btn--regular" @click="$emit('cancelClick')">Back</ButtonElement>
            <ButtonElement class="btn--ms btn--accent" @click="clickContinue()"
                           :is-disable="loadError || selectedGroups.length === 0 || selectedFeatures.length === 0 || selectedSubfeatures.length === 0">
              Continue
            </ButtonElement>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue'
import ButtonElement from '@shared/components/elements/button/button.element.vue'
import FieldSkeletonComponent from '@domains/settings/views/integrations/field-skeleton.component.vue'
import {type DropdownNs, FeatureNs, IntegrationNs} from '@shared/types'
import {mapStores} from "pinia"
import {useWorkspaceStore} from "@shared/stores/workspace.store"
import {useIntegrationStore} from "@domains/settings/stores/integration.store"
import {JiraMultipleSelectDropdownElement} from "@shared/components"
import {useGeneralStore} from "@shared/stores/general.store"
import {toast} from '@shared/ms-components'

export default defineComponent({
  name: 'AzureBoardsStep3',
  components: {
    ButtonElement,
    JiraMultipleSelectDropdownElement,
    FieldSkeletonComponent,
  },
  emits: ['cancelClick', 'continueClick'],
  props: {
    selectedProjectKey: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapStores(useWorkspaceStore, useIntegrationStore, useGeneralStore),
  },
  data() {
    return {
      isLoading: true,
      loadError: false,
      loadErrorMessage: '',
      intIssueTypes: [] as IntegrationNs.IntIssueType[],
      groupActions: [] as DropdownNs.DropdownOptionsItem[],
      featureActions: [] as DropdownNs.DropdownOptionsItem[],
      subfeatureActions: [] as DropdownNs.DropdownOptionsItem[],

      selectedGroups: [] as DropdownNs.DropdownOptionsItem[],
      selectedFeatures: [] as DropdownNs.DropdownOptionsItem[],
      selectedSubfeatures: [] as DropdownNs.DropdownOptionsItem[],
    }
  },
  beforeMount() {
    this.loadWorkItemTypes()
  },
  methods: {
    loadWorkItemTypes() {
      this.isLoading = true
      this.loadError = false
      this.groupActions = []
      this.featureActions = []
      this.subfeatureActions = []

      const payload = {
        workspaceId: this.workspaceStore.getSelectedWorkspaceId,
        integrationType: IntegrationNs.IntegrationTypeEnums.AZURE,
        projectKey: this.selectedProjectKey,
      } as IntegrationNs.GetIssueTypeRequest

      this.integrationStore.loadIssueTypes(payload).then((data) => {
        this.isLoading = false
        this.intIssueTypes = data
        if (data && data.length > 0) {
          this.intIssueTypes.forEach((issueType) => {
            const item: DropdownNs.DropdownOptionsItem = {
              name: issueType.name,
              svg: `${issueType.iconUrl}`,
              value: `${issueType.id}`,
            }
            if (issueType.hierarchyLevel === 1) {
              this.groupActions.push(item)
            } else if (issueType.hierarchyLevel === 0) {
              this.featureActions.push(item)
            } else if (issueType.hierarchyLevel === -1) {
              this.subfeatureActions.push(item)
            }
          })
        } else {
          toast.error('Unable to read Work Item Types from the project. Please try again')
          this.$emit('cancelClick')
        }
      }).catch((error) => {
        this.isLoading = false
        const status = error?.response?.status
        if (status === 401) {
          toast.error('Session expired. Please reconnect your Azure Boards account.')
          this.$emit('cancelClick')
          return
        }
        this.loadError = true
        this.loadErrorMessage = error?.response?.data?.message ?? 'Failed to load work item types. Please retry.'
      })
    },
    clickContinue() {
      if (this.selectedGroups.length === 0 || this.selectedFeatures.length === 0 || this.selectedSubfeatures.length === 0) return

      const groupMapping = this.intIssueTypes
        .filter((t) => this.selectedGroups.map((i) => i.value).includes(t.id))
        .map((t) => ({featureType: FeatureNs.FeatureTypeEnums.GROUP, issueTypeId: t.id, issueTypeName: t.name} as IntegrationNs.IntIssueTypeMapping))

      const featureMapping = this.intIssueTypes
        .filter((t) => this.selectedFeatures.map((i) => i.value).includes(t.id))
        .map((t) => ({featureType: FeatureNs.FeatureTypeEnums.FEATURE, issueTypeId: t.id, issueTypeName: t.name} as IntegrationNs.IntIssueTypeMapping))

      const subFeatureMapping = this.intIssueTypes
        .filter((t) => this.selectedSubfeatures.map((i) => i.value).includes(t.id))
        .map((t) => ({featureType: FeatureNs.FeatureTypeEnums.SUB_FEATURE, issueTypeId: t.id, issueTypeName: t.name} as IntegrationNs.IntIssueTypeMapping))

      this.$emit('continueClick', {GROUP: groupMapping, FEATURE: featureMapping, SUB_FEATURE: subFeatureMapping})
    },
  },
})
</script>

<style lang="scss" scoped>
.jf-row {
  .col-5 {
    flex: 0 0 auto;
    width: 48%;
  }

  .col-2 {
    flex: 0 0 auto;
    width: 2.66667%;
  }
}

h2 {
  margin-bottom: var(--space-32);
}

.jira-feature-form {
  min-height: 44px;
  margin-bottom: var(--space-8);

  .icon-snyc {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: var(--fs-14);
    color: var(--neutral-200);
    height: 44px;
  }

  .fp-label {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    height: 44px;
    border-radius: $br-sm;
    font-size: var(--fs-14);
    color: var(--grey-color--900);
    background: var(--light-300);
    border: 1px solid var(--light-300);

    .color {
      width: 14px;
      height: 14px;
      border-radius: $br-xs;
      background-color: var(--text-neutral);
    }

    .icon {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 44px;
      color: var(--text-neutral);
    }
  }
}

.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-12);
  padding: var(--space-32);
  text-align: center;
  font-size: var(--fs-14);
  color: var(--text-neutral);

  .error-icon {
    font-size: var(--fs-24);
    color: var(--danger-color);
  }
}
</style>
